/**
 * BugReportModal — In-app bug reporter
 *
 * Triggered from a sidebar button. Captures browser context automatically.
 * Optionally attaches last 5 API calls (snapshotted server-side).
 *
 * Uses semantic tokens throughout (no raw gray-* / slate-*).
 * Follows SpeyBooks component patterns: .input, .label, .btn-primary, .btn-secondary.
 *
 * File: web/src/components/BugReportModal.tsx
 * Version: v3.3.0
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import toast from 'react-hot-toast';

// ============================================================================
// Types
// ============================================================================

type Severity = 'critical' | 'high' | 'medium' | 'low';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const SEVERITY_OPTIONS: { value: Severity; label: string; description: string }[] = [
  { value: 'critical', label: 'Critical', description: 'Data loss, cannot use app' },
  { value: 'high', label: 'High', description: 'Major feature broken' },
  { value: 'medium', label: 'Medium', description: 'Something isn\'t right' },
  { value: 'low', label: 'Low', description: 'Minor cosmetic issue' },
];

const SEVERITY_COLOURS: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
  high: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  medium: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800', // status-pass
  low: 'bg-surface-tertiary text-content-secondary border-edge',
};

// ============================================================================
// Component
// ============================================================================

export default function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const location = useLocation();
  const titleRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [includeApiContext, setIncludeApiContext] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Focus title on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => titleRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setIncludeApiContext(true);
  }, []);

  const handleSubmit = async () => {
    if (!title.trim() || title.trim().length < 5) {
      toast.error('Title must be at least 5 characters');
      titleRef.current?.focus();
      return;
    }

    setSubmitting(true);

    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      theme: document.documentElement.classList.contains('dark') ? 'dark' as const : 'light' as const,
    };

    try {
      await api.post('/bug-reports', {
        title: title.trim(),
        description: description.trim(),
        severity,
        pageUrl: location.pathname,
        browserInfo,
        includeApiContext,
      });

      toast.success('Bug report submitted — we\'ll look into it');
      resetForm();
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || 'Failed to submit report';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-surface rounded-xl shadow-lg border border-edge w-full max-w-lg"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-report-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-edge">
            <div>
              <h2 id="bug-report-title" className="text-lg font-semibold text-content">
                Report a Bug
              </h2>
              <p className="text-sm text-content-tertiary mt-0.5">
                {location.pathname}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-content-tertiary hover:text-content transition-colors p-1 -m-1"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">

            {/* Title */}
            <div>
              <label htmlFor="bug-title" className="label">What went wrong?</label>
              <input
                ref={titleRef}
                id="bug-title"
                type="text"
                className="input mt-1"
                placeholder="e.g. VAT calculation wrong on Flat Rate invoices"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                disabled={submitting}
              />
              <p className="text-xs text-content-quaternary mt-1 text-right">{title.length}/200</p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="bug-description" className="label">
                Details <span className="font-normal text-content-quaternary">(optional)</span>
              </label>
              <textarea
                id="bug-description"
                className="input mt-1 min-h-[100px] resize-y"
                placeholder="Steps to reproduce, expected vs actual behaviour..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={5000}
                disabled={submitting}
              />
            </div>

            {/* Severity */}
            <div>
              <span className="label">Severity</span>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {SEVERITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSeverity(opt.value)}
                    disabled={submitting}
                    className={`
                      px-2 py-2 rounded-lg border text-center transition-all text-xs font-medium
                      ${severity === opt.value
                        ? SEVERITY_COLOURS[opt.value]
                        : 'border-edge text-content-tertiary hover:border-edge-strong hover:text-content-secondary'
                      }
                    `}
                    title={opt.description}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-content-quaternary mt-1">
                {SEVERITY_OPTIONS.find(o => o.value === severity)?.description}
              </p>
            </div>

            {/* API Context Toggle */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeApiContext}
                onChange={(e) => setIncludeApiContext(e.target.checked)}
                disabled={submitting}
                className="mt-0.5 h-4 w-4 rounded border-edge-strong text-heather-600 focus:ring-2 focus:ring-heather-500"
              />
              <div>
                <span className="text-sm font-medium text-content">Attach recent API calls</span>
                <p className="text-xs text-content-tertiary mt-0.5">
                  Includes your last 5 API requests for debugging context. No financial data is shared.
                </p>
              </div>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-edge bg-surface-secondary rounded-b-xl">
            <p className="text-xs text-content-quaternary">
              Browser info captured automatically
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary text-sm px-4 py-2"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary text-sm px-4 py-2"
                disabled={submitting || title.trim().length < 5}
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Submitting...
                  </span>
                ) : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Sidebar Trigger Button (export for use in Layout.tsx)
// ============================================================================

export function BugReportTrigger({
  collapsed = false,
  onClick,
}: {
  collapsed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-content-secondary hover:bg-surface-hover rounded-lg transition-colors"
      title="Report a bug"
    >
      <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      {!collapsed && <span>Report Bug</span>}
    </button>
  );
}
